/* eslint-disable max-len */
import { ModelSnippetFn, Language } from '../types';

const pythonSDK: ModelSnippetFn = (props) => `
from clarifai.client.model import Model

image_url = "https://s3.amazonaws.com/samples.clarifai.com/featured-models/image-captioning-statue-of-liberty.jpeg"

model_url = "https://clarifai.com/${props.userId}/${props.appId}/models/${props.modelId}"
model_prediction = Model(url=model_url, pat="${props.pat}").predict_by_url(image_url${
  props.deploymentId ? `, deployment_id="${props.deploymentId}"` : ''
})

print(model_prediction.outputs[0].data.text.raw)
`;

const python: ModelSnippetFn = (props) => `
##################################################################################################
# In this section, we set the user authentication, user and app ID, model details, and the URL
# of the image we want as an input. Change these strings to run your own example.
#################################################################################################

# Your PAT (Personal Access Token) can be found in the Account's Security section
PAT = '${props.pat}'
# Specify the correct user_id/app_id pairings
# Since you're making inferences outside your app's scope
USER_ID = '${props.userId}'
APP_ID = '${props.appId}'
# Change these to whatever model and image URL you want to use
MODEL_ID = '${props.modelId}'
MODEL_VERSION_ID = '${props.modelVersionId}'
IMAGE_URL = '${props.inputUrl}'

############################################################################
# YOU DO NOT NEED TO CHANGE ANYTHING BELOW THIS LINE TO RUN THIS EXAMPLE
############################################################################

from clarifai_grpc.channel.clarifai_channel import ClarifaiChannel
from clarifai_grpc.grpc.api import resources_pb2, service_pb2, service_pb2_grpc
from clarifai_grpc.grpc.api.status import status_code_pb2

channel = ClarifaiChannel.get_grpc_channel()
stub = service_pb2_grpc.V2Stub(channel)

metadata = (('authorization', 'Key ' + PAT),)

userDataObject = resources_pb2.UserAppIDSet(user_id=USER_ID, app_id=APP_ID)

post_model_outputs_response = stub.PostModelOutputs(
    service_pb2.PostModelOutputsRequest(
        user_app_id=userDataObject,  # The userDataObject is created in the overview and is required when using a PAT
        model_id=MODEL_ID,
        version_id=MODEL_VERSION_ID,  # This is optional. Defaults to the latest model version
        inputs=[
            resources_pb2.Input(
                data=resources_pb2.Data(
                    image=resources_pb2.Image(
                        url=IMAGE_URL
                    )
                )
            )
        ]
    ),
    metadata=metadata
)
if post_model_outputs_response.status.code != status_code_pb2.SUCCESS:
    print(post_model_outputs_response.status)
    raise Exception("Post model outputs failed, status: " + post_model_outputs_response.status.description)

# Since we have one input, one output will exist here
output = post_model_outputs_response.outputs[0]

# Get the output
print(output.data.text.raw)

# Uncomment this line to print the full Response JSON
#print(output)
`;

const nodejs: ModelSnippetFn = (props) => `
//index.js file

///////////////////////////////////////////////////////////////////////////////////////////////////
// In this section, we set the user authentication, user and app ID, model details, and the URL
// of the image we want as an input. Change these strings to run your own example.
///////////////////////////////////////////////////////////////////////////////////////////////////

// Your PAT (Personal Access Token) can be found in the Account's Security section
const PAT = '${props.pat}';
// Specify the correct user_id/app_id pairings
// Since you're making inferences outside your app's scope
const USER_ID = '${props.userId}';
const APP_ID = '${props.appId}';
// Change these to whatever model and image URL you want to use
const MODEL_ID = '${props.modelId}';
const MODEL_VERSION_ID = '${props.modelVersionId}';
const IMAGE_URL = '${props.inputUrl}';

///////////////////////////////////////////////////////////////////////////////////
// YOU DO NOT NEED TO CHANGE ANYTHING BELOW THIS LINE TO RUN THIS EXAMPLE
///////////////////////////////////////////////////////////////////////////////////

const { ClarifaiStub, grpc } = require("clarifai-nodejs-grpc");

const stub = ClarifaiStub.grpc();

// This will be used by every Clarifai endpoint call
const metadata = new grpc.Metadata();
metadata.set("authorization", "Key " + PAT);

stub.PostModelOutputs(
    {
        user_app_id: {
            "user_id": USER_ID,
            "app_id": APP_ID
        },
        model_id: MODEL_ID,
        version_id: MODEL_VERSION_ID, // This is optional. Defaults to the latest model version
        inputs: [
            { data: { image: { url: IMAGE_URL, allow_duplicate_url: true } } }
        ]
    },
    metadata,
    (err, response) => {
        if (err) {
            throw new Error(err);
        }

        if (response.status.code !== 10000) {
            throw new Error("Post model outputs failed, status: " + response.status.description);
        }

        // Since we have one input, one output will exist here
        const output = response.outputs[0];

        console.log(output?.data?.text?.raw);
    }

);
`;

const nodejs_SDK: ModelSnippetFn = (props) => `
import { Model } from "clarifai-nodejs";

const modelUrl = "https://clarifai.com/${props.userId}/${props.appId}/models/${props.modelId}"
const imageUrl =
  "https://s3.amazonaws.com/samples.clarifai.com/featured-models/image-captioning-statue-of-liberty.jpeg";


const model = new Model({
  url: modelUrl,
  authConfig: {
    pat: process.env.CLARIFAI_PAT,
  },
});
const modelPrediction = await model.predictByUrl({
  url: imageUrl,
  inputType: "image",
  ${props.deploymentId ? `deployment_id="${props.deploymentId}"` : ''}
});

// Get the output
console.log(modelPrediction?.[0]?.data?.text?.raw);
`;

export const imageToTextSnippets = {
  [Language.Python]: python,
  [Language.PythonSDK]: pythonSDK,
  [Language.NodeJS]: nodejs,
  [Language.NodeJS_SDK]: nodejs_SDK,
};
